import { VOICE_BITS_PER_SECOND, VOICE_WAVEFORM_BARS } from '@anon-chat/shared';

/**
 * Столбики в компактную строку: массив чисел раздул бы и запрос, и колонку в
 * базе. Каждый столбик — один байт, вся картинка укладывается в 64 символа.
 */
export function encodeWaveform(levels: number[]): string {
  if (levels.length === 0) {
    return '';
  }

  const peak = Math.max(...levels, 0.01);
  const step = levels.length / VOICE_WAVEFORM_BARS;
  const bars = new Uint8Array(VOICE_WAVEFORM_BARS);

  for (let index = 0; index < VOICE_WAVEFORM_BARS; index += 1) {
    const from = Math.floor(index * step);
    const to = Math.max(from + 1, Math.floor((index + 1) * step));
    let loudest = 0;
    for (let i = from; i < to && i < levels.length; i += 1) {
      loudest = Math.max(loudest, levels[i] ?? 0);
    }
    bars[index] = Math.round((loudest / peak) * 255);
  }

  return btoa(String.fromCharCode(...bars));
}

/** Обратное преобразование: строка в доли от нуля до единицы. */
export function decodeWaveform(encoded: string | null): number[] {
  if (!encoded) {
    return [];
  }

  try {
    return Array.from(atob(encoded), (char) => char.charCodeAt(0) / 255);
  } catch {
    return [];
  }
}

/**
 * Запись голосового сообщения.
 *
 * Формат не выбирается, а согласуется с браузером: Android и настольные пишут
 * webm/opus, техника Apple — mp4/aac. Сервер принимает оба и определяет тип по
 * сигнатуре файла, поэтому здесь достаточно взять первый поддерживаемый.
 */
const PREFERRED_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export interface Recording {
  blob: Blob;
  durationSec: number;
  mimeType: string;
  /** Столбики громкости, base64: рисуются в пузыре у обеих сторон. */
  waveform: string;
}

export function isVoiceSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

function pickMimeType(): string | undefined {
  return PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;

  // Громкость снимается по ходу записи, а не разбором готового файла: разбор
  // пятнадцати минут на телефоне — это секунды ожидания и лишняя память.
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private levelTimer: number | null = null;
  private levels: number[] = [];

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  /**
   * Запрашивает микрофон и начинает запись. Отказ в доступе поднимается наружу:
   * человеку нужно объяснить, что делать, а не молча ничего не записать.
   */
  async start(): Promise<void> {
    /*
      Подавление шума выключено намеренно: на речи оно съедает верхние частоты,
      и голос звучит глухо, «как из бутылки». Эхоподавление и автоусиление
      оставлены — они не портят тембр, а разговор в помещении улучшают.
    */
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: false,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(this.stream, {
      // Без явного битрейта браузер берёт около 128 кбит/с: для речи это вчетверо
      // больше нужного, и четверть часа весит уже десятки мегабайт.
      audioBitsPerSecond: VOICE_BITS_PER_SECOND,
      ...(mimeType ? { mimeType } : {}),
    });

    this.chunks = [];
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    this.startLevelCapture(this.stream);
    this.startedAt = Date.now();
    this.recorder.start();
  }

  /** Останавливает запись и отдаёт результат. */
  async stop(): Promise<Recording | null> {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') {
      this.release();
      return null;
    }

    const finished = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });

    recorder.stop();
    await finished;

    const durationSec = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000));
    const mimeType = recorder.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mimeType });
    const waveform = encodeWaveform(this.levels);

    this.release();
    return blob.size > 0 ? { blob, durationSec, mimeType, waveform } : null;
  }

  /** Отмена: запись останавливается, ничего не возвращается. */
  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.release();
  }

  /**
   * Замер громкости раз в сотню миллисекунд. Значения копятся как есть, а
   * сжимаются до нужного числа столбиков уже при отправке: длительность записи
   * заранее неизвестна.
   */
  private startLevelCapture(stream: MediaStream): void {
    try {
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);

      const buffer = new Uint8Array(analyser.frequencyBinCount);
      this.levels = [];
      this.levelTimer = window.setInterval(() => {
        analyser.getByteTimeDomainData(buffer);
        // Среднеквадратичное отклонение от тишины: слышимая громкость, а не
        // мгновенное значение сигнала.
        let sum = 0;
        for (const sample of buffer) {
          const centred = (sample - 128) / 128;
          sum += centred * centred;
        }
        this.levels.push(Math.sqrt(sum / buffer.length));
      }, 100);

      this.audioContext = context;
      this.analyser = analyser;
    } catch {
      // Без графика запись всё равно должна работать: столбики — украшение.
      this.levels = [];
    }
  }

  /**
   * Микрофон освобождается всегда. Оставленная дорожка держит красный значок
   * записи в браузере и на телефоне — человек решит, что его слушают.
   */
  private release(): void {
    if (this.levelTimer !== null) {
      window.clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
    void this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;
    this.analyser = null;

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }

  /** Текущая громкость для анимации кнопки: последнее снятое значение. */
  get level(): number {
    return this.levels.length > 0 ? (this.levels[this.levels.length - 1] ?? 0) : 0;
  }
}
